import { BasketEvent } from '@gn-audio/sfcc-basket/enums';
import { IBasketEventBus } from '@gn-audio/sfcc-basket/models';
import { ICgGroup, ISolutionBundleItem } from '@gn-audio/core/api/compatibility-guide/models';
import { addToCart } from '@gn-audio/core/api/e-commerce/add-to-cart';
import { fetchGroupPrice } from '@gn-audio/core/api/pricing/fetch-group-price';
import { IFromPrice, ISFCCGroupPrice } from '@gn-audio/core/api/pricing/models';
import { getAvailabilityState } from '@gn-audio/core/api/pricing/utilities';
import { IGroup } from '@gn-audio/core/api/product-catalogue/models/group';
import { fetchEcomSetting } from '@gn-audio/core/api/site-settings/fetch-ecom-setting';
import { IEcomSetting } from '@gn-audio/core/api/site-settings/models/ecom-setting';
import { AvailabilityState, SegmentType } from '@gn-audio/core/common/enums';
import { Component, Inject, Input, OnChanges } from 'angular-ts-decorators';
import { TranslationKey } from 'src/Foundation/Translation/platform';

import { ScrollTargetSelector } from '../models';
import { getComingSoonDictionaryKey, scrollToTarget } from '../utils';

import template from './cg-customizer-add-to-cart.component.html';

declare var BasketEventBus: IBasketEventBus;

@Component({
  selector: 'cgCustomizerAddToCart',
  template
})
export class CgCustomizerAddToCartComponent implements OnChanges {
  public static $inject = ['$q'];

  @Input() public readonly group?: IGroup;
  @Input() public readonly bundles?: ISolutionBundleItem[];

  public groupState?: AvailabilityState;
  public readonly TranslationKey = TranslationKey;

  constructor(@Inject('$q') private readonly $q: ng.IQService) { }

  public ngOnChanges(): void {
    if (!this.group) {
      return;
    }

    this.$q.when(fetchEcomSetting())
      .then((ecomSetting: IEcomSetting | null) => {
        if (!ecomSetting?.ecomEnabled) {
          this.groupState = AvailabilityState.WhereToBuy;
          return;
        }

        this.$q.when(fetchGroupPrice(this.group.groupId))
          .then((price: IFromPrice | ISFCCGroupPrice | null) => {
            this.groupState = getAvailabilityState(price);
          });
      });
  }

  public add(): void {
    if (!this.group || isNaN(this.group.groupId)) {
      return;
    }

    if (this.isWhereToBuyState) {
      return;
    }

    const skus: string[] = [this.group.sku];
    (this.bundles || []).forEach(
      (b: ISolutionBundleItem): void => {
        const bundleSkus: string[] = b.bundle.map((g: ICgGroup): string => g.sku);
        Array.prototype.push.apply(skus, bundleSkus);
      },
    );

    if (typeof BasketEventBus === 'undefined') {
      addToCart(skus);
    } else {
      BasketEventBus.post(BasketEvent.AddToBasket, skus);
    }
  }

  public onButtonClick(): void {
    scrollToTarget(document.querySelector(ScrollTargetSelector.Form));
  }

  public get isWhereToBuyState(): boolean {
    return this.groupState === AvailabilityState.WhereToBuy;
  }

  public get isComingSoonState(): boolean {
    return this.groupState === AvailabilityState.ComingSoon;
  }

  public get isAddToCartVisible(): boolean {
    return !this.isWhereToBuyState && !this.isComingSoonState;
  }

  public get whereToBuyUrl(): string | undefined {
    if (!this.group) {
      return undefined;
    }

    switch (this.segmentType) {
      case SegmentType.Business:
        return '/where-to-buy/partner-locator';

      default:
        return '/where-to-buy/retailer-locator';
    }
  }

  public get dictionaryKey(): TranslationKey | undefined {
    return this.isComingSoonState ? getComingSoonDictionaryKey(this.segmentType) : undefined;
  }

  private get segmentType(): SegmentType | undefined {
    return this.group?.segmentType;
  }
}
